/**
 * Adumo Payment Service
 * Handles communication with backend for JWT-based payment processing
 */

// Configure your Java backend URL here
const BACKEND_API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';

/**
 * Validate KP Card before payment
 * @param {string} cardNumber - The 16-digit KP Card number
 * @returns {Promise<Object>} Validation result
 */
export const validateCard = async (cardNumber) => {
  try {
    const response = await fetch(`${BACKEND_API_URL}/api/kpcard/validate-card?cardNumber=${cardNumber}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const data = await response.json();
    
    if (!response.ok) {
      return {
        valid: false,
        message: data.message || 'Failed to validate card'
      };
    }

    return data;

  } catch (error) {
    console.error('Error validating card:', error);
    return {
      valid: false,
      message: 'Unable to connect to server. Please try again.'
    };
  }
};

/**
 * Initiate payment with Adumo through backend
 * @param {Object} paymentData - Payment details
 * @returns {Promise<Object>} Payment link and parameters with JWT token
 */
export const initiatePayment = async (paymentData) => {
  try {
    const response = await fetch(`${BACKEND_API_URL}/api/kpcard/initiate-recharge`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        cardNumber: paymentData.cardNumber,
        amount: paymentData.amount,
        currency: paymentData.currency,
        email: paymentData.email,
        tenantId: paymentData.tenantId || 'DEFAULT'
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to initiate payment');
    }

    const data = await response.json();
    return data;

  } catch (error) {
    console.error('Error initiating payment:', error);
    throw error;
  }
};

/**
 * Submit payment form to Adumo with parameters from backend
 * @param {string} paymentLink - Adumo payment URL
 * @param {Object} paymentParameters - Parameters including JWT token
 */
export const submitPaymentForm = (paymentLink, paymentParameters) => {
  console.log('=== SUBMITTING TO ADUMO WITH JWT ===');
  console.log('Payment URL:', paymentLink);
  console.log('Parameters:', Object.keys(paymentParameters));
  console.log('JWT Token included:', !!paymentParameters.Token);

  // Create form
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = paymentLink;
  form.setAttribute('accept-charset', 'UTF-8');

  // Add all parameters
  Object.entries(paymentParameters).forEach(([key, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = value;
    form.appendChild(input);
    
    // Log parameter (but truncate Token for security)
    if (key === 'Token') {
      console.log(`  ${key}: ${value.substring(0, 50)}...`);
    } else {
      console.log(`  ${key}: ${value}`);
    }
  });

  // Add to document and submit
  document.body.appendChild(form);
  
  setTimeout(() => {
    console.log('SUBMITTING FORM WITH JWT TOKEN...');
    form.submit();
  }, 100);
};

/**
 * Handle payment response (for success/failure pages)
 * @param {URLSearchParams} params - URL parameters from redirect
 * @returns {Object} Parsed payment response
 */
export const parsePaymentResponse = (params) => {
  return {
    merchantReference: params.get('MerchantReference'),
    transactionIndex: params.get('TransactionIndex'),
    result: params.get('_RESULT'),
    amount: params.get('Amount'),
    currency: params.get('AuthoriseCurrencyCode'),
    responseToken: params.get('_RESPONSE_TOKEN'),
    // Add other parameters as needed
  };
};

export default {
  validateCard,
  initiatePayment,
  submitPaymentForm,
  parsePaymentResponse
};





